import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { EmptyState, InfoBox, LoadingState } from '@biznomi/ui-react'
import { getUntaggedToday } from '@/lib/api/admin'

/**
 * 오늘 아무 기록도 없는 사람.
 *
 * 대시보드 "기록 없음" 은 숫자만 준다. 그 숫자로는 할 수 있는 게 없어서 명단을 따로 둔다.
 *
 * **판정하지 않는다.** 결근·무단결근이 아니라 "아직 기록이 없다"는 사실이다.
 * 휴가를 안 올렸을 수도, 늦게 올 수도, 외근 중일 수도 있다. 그건 사람이 안다.
 * 그래서 여기에는 경고색도, 조치 버튼도 두지 않는다. 이름을 눌러 상세로 갈 뿐이다.
 */
export default function Untagged() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'untagged-today'],
    queryFn: async () => {
      const res = await getUntaggedToday()
      if (!res.success || !res.data) throw new Error(res.error ?? '명단을 불러오지 못했습니다')
      return res.data
    },
    refetchInterval: 60_000,
  })

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>
  if (!data) return null

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold">기록 없음</h1>
        <p className="mt-1 text-sm text-slate-500">
          오늘 출근·퇴근·수기 신청·부재 어느 것도 없는 사람입니다
        </p>
      </header>

      {data.employees.length === 0 ? (
        <div className="rounded-card bg-card">
          <EmptyState title="모두 기록이 있습니다" icon="users" />
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
          {data.employees.map((e) => (
            <li key={e.id} className="px-4 py-3.5">
              <Link to={`/admin/employees/${e.id}`} className="font-medium hover:underline">
                {e.name}
              </Link>
              <p className="mt-0.5 text-xs text-slate-400">
                {[e.employeeNumber, e.departmentName].filter(Boolean).join(' · ') || '정보 없음'}
              </p>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs leading-relaxed text-slate-400">
        결근이라는 뜻이 아닙니다. 시스템은 사유를 판단하지 않습니다.
      </p>
    </div>
  )
}
